import React, { FC, ReactNode } from 'react';
import { TableInfo } from '../../../services/table/tableTypes';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../routers/Routes';
import { formatCurrency } from '@/pages/table/utils/utils';
import { replacePathParams } from '@/common/utils/utils';
import { InfoIcon } from 'lucide-react';
import TableStatus from '@/components/TableStatus/TableStatus';
import CurrencyIcon from '@/components/CurrencySymbolMask/CurrencyIcon';
import TableStakeInfo from '@/components/TableStakeInfo';

interface TableCardProp {
  table: TableInfo;
  setShowTableInfo: (value: boolean) => void;
  setSelectedRow: (value: TableInfo) => void;
  statusPadding?: boolean;
}

const TableCardItem = ({ value, title }: { value: string | ReactNode; title: string }) => (
  <div className="flex flex-col items-center justify-center text-white">
    <span className="font-manrope font-bold text-white"> {value}</span>
    <span className="font-manrope text-sm font-bold text-white text-opacity-30">{title}</span>
  </div>
);
const TableCard: FC<TableCardProp> = ({ table, setShowTableInfo, setSelectedRow, statusPadding = true }) => {
  const navigate = useNavigate();

  const onClickCardHandler = () => navigate(replacePathParams(ROUTES.TABLE, { tableId: table.id || '' }));

  const onInfoIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTableInfo(true);
    setSelectedRow(table);
  };

  return (
    <div
      className="left-4 w-full rounded-xl bg-phenom-border-glow p-[1px] hover:cursor-pointer"
      onClick={onClickCardHandler}
    >
      <div className={twMerge('h-full w-full gap-10 rounded-xl bg-ph-violet-medium')}>
        <div className="flex w-full justify-between px-5 py-3">
          <div className="flex w-full gap-6">
            <TableStatus
              playerCount={table?.players!}
              maxPlayer={table?.maxPlayers!}
              tableName={table?.name!}
              tableGame={table?.game!}
              padding={statusPadding}
            />
            {table?.variants?.length > 1 && (
              <div className="ml-auto">
                <InfoIcon className="text-white text-opacity-30" onClick={onInfoIconClick} />
              </div>
            )}
          </div>
        </div>
        <div className="flex w-full flex-col flex-wrap justify-center justify-around gap-4 rounded-b-xl bg-ph-purple-dark px-5 py-2.5 sm:flex-row sm:gap-1">
          <div className="flex flex-1 flex-row items-center justify-between gap-4">
            <div className="flex-1">
              <TableCardItem value={<TableStakeInfo fontSize="text-sm" table={table} />} title={'Stakes'} />
            </div>

            <div className="flex-1">
              <TableCardItem
                value={
                  <span className="flex flex-row items-center gap-0.5 text-sm">
                    <span className="flex flex-row items-center">
                      <CurrencyIcon />
                      {table.minBuyin}
                    </span>
                    <span className="mx-2 font-bold">/</span>
                    <span className="flex flex-row items-center gap-0.5">
                      <CurrencyIcon />
                      {table.maxBuyin}
                    </span>
                  </span>
                }
                title={'Buy In'}
              />
            </div>
          </div>
          <span className="flex flex-1 flex-row items-center justify-between">
            <div className="flex-1">
              <TableCardItem
                value={
                  <span className="flex flex-row items-center text-sm">
                    <CurrencyIcon />
                    {formatCurrency(table.avgPot)}
                  </span>
                }
                title={'Pot'}
              />
            </div>
            <div className="flex-1">
              <TableCardItem
                value={
                  <span className="text-sm">
                    {`${table.players}/${table.maxPlayers} ${table.waitlist.length > 0 ? `(${table.waitlist.length})` : ''}`}
                  </span>
                }
                title={'Players'}
              />
            </div>
          </span>
        </div>
      </div>
    </div>
  );
};

export default TableCard;
